import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Trash2, Eye, EyeOff, AlertTriangle, X, Download, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import HeaderNav from '@/components/HeaderNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LeadList() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [leads, setLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState(''); // Search is ephemeral - React state only, never persisted
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSegment, setCurrentSegment] = useState('All'); // Default to 'all' tab
  const [showAllFields, setShowAllFields] = useState(false);
  const [statusCounts, setStatusCounts] = useState({}); // Counts for each status in dropdown
  const [counts, setCounts] = useState({}); // Counts for tabs (All, New, Backlog, etc.)
  const [stateRestored, setStateRestored] = useState(false); // Track if state restoration is complete
  const previousLocationRef = useRef(location.pathname); // Track previous location to detect navigation
  
  // Filters - single select dropdowns + date range (Modification #9)
  const [filters, setFilters] = useState({
    project: '',
    status: '',
    source: '',
    salesRep: '',
    fromDate: '',
    toDate: ''
  });
  const [availableProjects, setAvailableProjects] = useState([]);
  const [availableSources, setAvailableSources] = useState([]); // Will store full source objects with {value, label}
  const [availableSalesReps, setAvailableSalesReps] = useState([]);
  const [availableDeadReasons, setAvailableDeadReasons] = useState([]); // Dead reasons for terminal statuses
  
  // PAGINATION: Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalLeads, setTotalLeads] = useState(0);
  const limit = 50; // Items per page
  
  // Status change dialog
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [statusChangeData, setStatusChangeData] = useState({
    leadId: null,
    newStatus: '',
    next_call_date: '',      // For Attempted 1-3, Follow up, Site Visit Planned
    site_visit_number: '',   // For Site Visit Done (1-4)
    site_visit_date: '',     // For Site Visit Done (date only)
    tentative_visit_date: '', // For Site Visit Planned
    comments: '',
    deadReason: ''
  });
  
  // Delete dialogs
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCredentialDialog, setShowCredentialDialog] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [deleteLeadIds, setDeleteLeadIds] = useState([]);

  // CHANGE 6: Bulk reassign dialog state
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [activeSalesReps, setActiveSalesReps] = useState([]);
  const [selectedSalesRep, setSelectedSalesRep] = useState('');

  const isSuperAdmin = user?.user_role === 'Super Administrator';
  const isManager = user?.user_role === 'Manager';
  const isSalesRep = user?.user_role === 'Sales Rep';
  const isAdminOrManager = isSuperAdmin || isManager;

  // Status options for filter dropdown
  const allStatuses = [
    'New', 'Attempted - 1', 'Attempted - 2', 'Attempted - 3',
    'Site Visit Planned', 'Site Visit Done', 'Follow up',
    'Booked', 'Lost', 'Unqualified', 'Dropped after site visit'
  ];

  // BUG FIX #2: Compute hasActiveFilters and track previous state for filter clearing detection
  // IMPORTANT: These must be defined BEFORE useEffects that use them
  const hasActiveFilters = filters.project || filters.status || filters.source || filters.salesRep || filters.fromDate || filters.toDate;
  const prevHasActiveFilters = useRef(hasActiveFilters);

  // FILTER PERSISTENCE: Save filter state to sessionStorage (NOT localStorage)
  // This ensures filters persist during back navigation but clear on browser close/logout
  // NOTE: searchTerm is NOT saved - it should be ephemeral (React state only)
  useEffect(() => {
    // Only save if we're on the leads page (don't save when on detail page)
    if (location.pathname === '/leads') {
      // Check if we just manually saved (within last 100ms) to avoid overwriting
      const lastManualSave = sessionStorage.getItem('lastManualSaveTime');
      const now = Date.now();
      
      if (lastManualSave && (now - parseInt(lastManualSave)) < 100) {
        console.log('⏭️ Skipping auto-save - manual save just happened');
        return;
      }
      
      const stateToSave = {
        filters,
        currentSegment,
        currentPage,
        showAllFields
        // searchTerm is intentionally NOT included
      };
      sessionStorage.setItem('leadListState', JSON.stringify(stateToSave));
      console.log('💾 Auto-saving filters to sessionStorage:', stateToSave);
    }
  }, [filters, currentSegment, currentPage, showAllFields, location.pathname]);

  // FILTER RESTORATION: Restore filter state from sessionStorage on mount and when returning to page
  useEffect(() => {
    const isOnLeadsPage = location.pathname === '/leads';
    const previousLocation = previousLocationRef.current;
    
    // Get the last pathname from sessionStorage
    const lastPath = sessionStorage.getItem('lastPathname');
    
    // Check if we came from a detail page (path starts with /leads/ and is different from current)
    // OR if previous location was a detail page (using ref)
    const wasOnDetailPage = (lastPath?.startsWith('/leads/') && lastPath !== location.pathname) ||
                            (previousLocation?.startsWith('/leads/') && previousLocation !== location.pathname);
    const isNavigatingBackToLeads = isOnLeadsPage && wasOnDetailPage;
    
    console.log('🔍 Filter restoration check:', {
      isOnLeadsPage,
      previousLocation,
      lastPath,
      wasOnDetailPage,
      isNavigatingBackToLeads,
      currentPath: location.pathname,
      stateRestored
    });
    
    // Update previous location ref BEFORE processing (so we can detect next navigation)
    previousLocationRef.current = location.pathname;
    
    // Don't process if not on leads page - let detail page handle its own pathname saving
    if (!isOnLeadsPage) {
      // Reset stateRestored so we can restore when coming back
      setStateRestored(false);
      return;
    }
    
    // Only restore if we're on the leads page
    if (isOnLeadsPage) {
      // Check for dashboard filter params first (these take priority - clear saved filters)
      const searchParams = new URLSearchParams(location.search);
      const filterParam = searchParams.get('filter');
      
      if (filterParam) {
        // Handle dashboard metric box navigation (Change 9)
        // When coming from dashboard, clear saved filters and use dashboard filters
        const today = format(new Date(), 'yyyy-MM-dd');
        
        switch (filterParam) {
          case 'backlog':
            setCurrentSegment('backlog');
            setFilters({
              project: '',
              status: '',
              source: '',
              salesRep: '',
              fromDate: '',
              toDate: ''
            });
            break;
          case 'site_visits_done':
            setFilters({
              project: '',
              status: 'Site Visit Done',
              source: '',
              salesRep: '',
              fromDate: '',
              toDate: ''
            });
            break;
          case 'today':
            setFilters({
              project: '',
              status: '',
              source: '',
              salesRep: '',
              fromDate: today,
              toDate: today
            });
            break;
          case 'pending_visits':
            setFilters({
              project: '',
              status: 'Site Visit Planned',
              source: '',
              salesRep: '',
              fromDate: '',
              toDate: ''
            });
            break;
          default:
            break;
        }
        
        // Clear the URL param after applying filter
        window.history.replaceState({}, '', '/leads');
        setStateRestored(true);
      } else {
        // Always try to restore from sessionStorage when on leads page
        // This ensures filters are restored both on initial mount and when navigating back
        // Try manual save first (if coming back from detail page), then fall back to regular save
        let savedState = null;
        if (isNavigatingBackToLeads) {
          // When coming back from detail page, use the manual save (which won't be overwritten)
          savedState = sessionStorage.getItem('leadListStateManual');
          console.log('📦 Saved state from sessionStorage (manual, for navigation back):', savedState ? 'Found' : 'Not found');
        }
        
        // Fall back to regular save if manual save not found
        if (!savedState) {
          savedState = sessionStorage.getItem('leadListState');
          console.log('📦 Saved state from sessionStorage (regular):', savedState ? 'Found' : 'Not found');
        }
        
        if (savedState) {
          try {
            const state = JSON.parse(savedState);
            console.log('📋 Parsed state:', state);
            
            // CRITICAL: Always restore when coming back from detail page, regardless of stateRestored
            // OR restore on initial mount if not restored yet
            const shouldRestore = isNavigatingBackToLeads || !stateRestored;
            
            if (shouldRestore) {
              if (isNavigatingBackToLeads) {
                console.log('✅ NAVIGATING BACK FROM DETAIL PAGE - FORCING RESTORE');
                console.log('🔍 Detection details:', { lastPath, previousLocation, currentPath: location.pathname });
              } else {
                console.log('✅ INITIAL MOUNT - RESTORING');
              }
              
              // Restore filters IMMEDIATELY - this is critical
              if (state.filters) {
                console.log('🔄 Restoring filters from saved state:', state.filters);
                console.log('🔄 Full saved state:', JSON.stringify(state, null, 2));
                
                // Create a new object to ensure React detects the change
                const restoredFilters = {
                  project: state.filters.project || '',
                  status: state.filters.status || '',
                  source: state.filters.source || '',
                  salesRep: state.filters.salesRep || '',
                  fromDate: state.filters.fromDate || '',
                  toDate: state.filters.toDate || ''
                };
                console.log('🔄 Restored filters object:', restoredFilters);
                console.log('🔄 Project filter value:', restoredFilters.project);
                
                // Always restore when coming back from detail page, even if filters appear empty
                // This is because the saved state might have been overwritten, but we should still try
                if (isNavigatingBackToLeads) {
                  // When coming back from detail page, always restore (even if empty)
                  // The user expects to see the same filters they had before
                  setFilters(restoredFilters);
                  console.log('✅ Filters restored (coming back from detail page):', restoredFilters);
                } else if (restoredFilters.project || restoredFilters.status || restoredFilters.source || 
                    restoredFilters.salesRep || restoredFilters.fromDate || restoredFilters.toDate) {
                  // On initial mount, only restore if filters are not empty
                  setFilters(restoredFilters);
                  console.log('✅ Filters restored successfully');
                } else {
                  console.log('⚠️ Saved filters are empty - not restoring to avoid clearing current filters');
                }
              } else {
                console.log('⚠️ No filters object in saved state');
              }
              
              // Restore segment
              if (state.currentSegment) {
                setCurrentSegment(state.currentSegment);
              }
              
              // Restore page
              if (state.currentPage) {
                setCurrentPage(state.currentPage);
              }
              
              // Restore column view
              if (state.showAllFields !== undefined) {
                setShowAllFields(state.showAllFields);
              }
              
              // DO NOT restore searchTerm - always starts empty
              
              // Mark state restoration as complete
              setStateRestored(true);
              console.log('✅ State restoration complete. Filters restored:', state.filters);
            } else {
              console.log('⏭️ Skipping restoration - already restored and not coming from detail page');
              console.log('🔍 Why skipped:', { isNavigatingBackToLeads, stateRestored, lastPath, previousLocation });
            }
          } catch (error) {
            console.error('❌ Error restoring lead list state:', error);
            setStateRestored(true);
          }
        } else {
          // No saved state, mark as restored anyway
          console.log('📭 No saved state found');
          if (!stateRestored) {
            setStateRestored(true);
          }
        }
      }
      
      // Update last pathname in sessionStorage AFTER processing
      // Use a small timeout to ensure restoration happens first, then update pathname
      if (isNavigatingBackToLeads) {
        // After restoring from detail page, update pathname after a brief delay
        setTimeout(() => {
          sessionStorage.setItem('lastPathname', location.pathname);
        }, 100);
      } else {
        // Normal case - update immediately
        sessionStorage.setItem('lastPathname', location.pathname);
      }
    }
  }, [location.pathname, location.search]); // Run on mount and when location changes

  useEffect(() => {
    // Only load data after state restoration is complete
    if (stateRestored) {
      console.log('Loading data with restored state:', { filters, searchTerm, currentSegment, currentPage });
      loadLeads();
      loadStatusCounts();
      loadCounts();
      loadFilterOptions();
    }
  }, [currentSegment, currentPage, stateRestored]); // Add stateRestored dependency

  // BUG FIX #2: Force data reload when filters are applied OR cleared after state restoration
  useEffect(() => {
    if (stateRestored) {
      // Reload if filters are active OR if filters were just cleared (prevHasActiveFilters was true, now false)
      const filtersJustCleared = prevHasActiveFilters.current && !hasActiveFilters;
      
      if (hasActiveFilters || filtersJustCleared) {
        console.log('Filters changed, reloading data. hasActiveFilters:', hasActiveFilters, 'filtersJustCleared:', filtersJustCleared);
        setCurrentPage(1); // Reset to page 1 when filters change
        loadLeads();
        // Reload status counts when filters change (to reflect filtered counts in dropdown)
        loadStatusCounts();
        // Reload tab counts when filters change (only if status filter is not applied)
        if (!filters.status) {
          loadCounts();
        }
      }
      
      // Update ref for next comparison
      prevHasActiveFilters.current = hasActiveFilters;
    }
  }, [filters, stateRestored, hasActiveFilters]);


  useEffect(() => {
    // Only reset page if state restoration is complete
    if (stateRestored) {
      setCurrentPage(1); // PAGINATION: Reset to page 1 when segment changes
    }
  }, [currentSegment, stateRestored]);

  useEffect(() => {
    // Only reset and load if state restoration is complete
    if (stateRestored) {
      setCurrentPage(1); // PAGINATION: Reset to page 1 when search term changes
      loadLeads();
    }
  }, [searchTerm, stateRestored]);

  // Log count comparison whenever relevant state changes
  useEffect(() => {
    if (stateRestored && filters.status) {
      console.log('=== COUNT COMPARISON SUMMARY ===');
      console.log('Current Status Filter:', filters.status);
      console.log('Current Segment:', currentSegment);
      console.log('Active Filters:', filters);
      console.log('');
      console.log('1. Filter Chip Count (totalLeads):', totalLeads);
      console.log('   - Source: loadLeads() with segment=' + filters.status);
      console.log('   - This shows ONLY leads with status="' + filters.status + '"');
      console.log('');
      console.log('2. Tab Count (counts.lost):', counts.lost || 'N/A');
      console.log('   - Source: loadCounts() with segment="lost"');
      console.log('   - This shows leads with status IN ["Lost", "Unqualified", "Dropped after site visit"]');
      console.log('   - NOTE: "lost" segment includes 3 statuses, not just "Lost"!');
      console.log('');
      console.log('3. Status Count (statusCounts[filters.status]):', statusCounts[filters.status] || 'N/A');
      console.log('   - Source: loadStatusCounts() with segment=' + filters.status);
      console.log('   - This shows leads with status="' + filters.status + '" (excluding duplicates)');
      console.log('');
      console.log('=== WHY THEY DIFFER ===');
      if (filters.status === 'Lost') {
        console.log('When status="Lost":');
        console.log('  - Filter chip: Shows ONLY "Lost" status leads');
        console.log('  - Tab "lost": Shows "Lost" + "Unqualified" + "Dropped after site visit"');
        console.log('  - This is why tab count is HIGHER than filter chip count');
      } else {
        console.log('Status filter is:', filters.status);
        console.log('Check if this status is part of a segment that includes multiple statuses');
      }
      console.log('========================');
    }
  }, [filters.status, totalLeads, counts.lost, statusCounts, stateRestored, currentSegment, filters]);

  const loadLeads = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const params = {};
      // If status filter is applied, use segment parameter with status value. Otherwise use currentSegment
      if (filters.status) {
        // Status filter - use segment parameter with status value
        params.segment = filters.status;
      } else {
        // No status filter - use segment tabs
        params.segment = currentSegment || 'All';
      }
      if (filters.project) params.project = filters.project;
      if (filters.source) params.source = filters.source;
      if (filters.salesRep && isAdminOrManager) params.assigned_to = filters.salesRep;
      if (filters.fromDate) params.from_date = filters.fromDate;  // Modification #9
      if (filters.toDate) params.to_date = filters.toDate;  // Modification #9
      if (searchTerm.trim()) params.search = searchTerm.trim(); // Add search parameter
      
      // PAGINATION: Add pagination parameters
      params.page = currentPage;
      params.limit = limit;
      
      console.log('Loading leads with params:', params);
      console.log('Filters state:', filters);
      
      const response = await axios.get(`${API}/leads`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      // PAGINATION: Update state from paginated response
      setLeads(response.data.leads || []);
      setTotalLeads(response.data.total || 0);
      setTotalPages(response.data.pages || 0);
      console.log('=== LOAD LEADS ===');
      console.log('Params used:', params);
      console.log('Total leads (totalLeads - used in filter chips):', response.data.total || 0);
      console.log('Received leads count:', response.data.leads?.length || 0);
      console.log('Current filters:', filters);
      console.log('Current segment:', currentSegment);
    } catch (error) {
      console.error('Load leads error:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const loadStatusCounts = async () => {
    const token = localStorage.getItem('token');
    try {
      const counts = {};
      
      // Build base params with all filters EXCEPT status filter
      const baseParams = {};
      if (filters.project) baseParams.project = filters.project;
      if (filters.source) baseParams.source = filters.source;
      if (filters.salesRep && isAdminOrManager) baseParams.assigned_to = filters.salesRep;
      if (filters.fromDate) baseParams.from_date = filters.fromDate;
      if (filters.toDate) baseParams.to_date = filters.toDate;
      if (searchTerm.trim()) baseParams.search = searchTerm.trim();
      
      // Always use segment='all' to exclude duplicates (matching tab behavior)
      baseParams.segment = 'All';
      
      // If no filters are active, use backend counts for "All" to ensure accuracy
      if (!hasActiveFilters) {
        console.log('=== LOAD STATUS COUNTS (No Filters) ===');
        // Get "All" count from backend counts endpoint (matches tab)
        const countsResponse = await axios.get(`${API}/leads/counts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        counts['All'] = countsResponse.data.all || 0;
        console.log('All count from /leads/counts:', counts['All']);
        
        // For individual status counts, query each status with segment parameter
        // Use limit=1 and get total from response to avoid loading all leads
        for (const status of allStatuses) {
          try {
            const statusParams = { ...baseParams, segment: status, limit: 1 };
            const statusResponse = await axios.get(`${API}/leads`, {
              headers: { Authorization: `Bearer ${token}` },
              params: statusParams
            });
            counts[status] = statusResponse.data.total || 0;
            console.log(`Status "${status}" count (segment parameter):`, counts[status], 'Params:', statusParams);
          } catch (error) {
            console.error(`Failed to load count for status ${status}:`, error);
            counts[status] = 0;
          }
        }
      } else {
        console.log('=== LOAD STATUS COUNTS (Filters Active) ===');
        console.log('Active filters:', filters);
        console.log('Base params:', baseParams);
        
        // Filters are active - need to get counts with filters applied
        // Get "All" count with filters
        const allParams = { ...baseParams, limit: 1 };
        const allResponse = await axios.get(`${API}/leads`, {
          headers: { Authorization: `Bearer ${token}` },
          params: allParams
        });
        counts['All'] = allResponse.data.total || 0;
        console.log('All count with filters (segment="all"):', counts['All'], 'Params:', allParams);
        
        // For each status, query with filters + status using segment parameter
        for (const status of allStatuses) {
          try {
            const statusParams = { ...baseParams, segment: status, limit: 1 };
            const statusResponse = await axios.get(`${API}/leads`, {
              headers: { Authorization: `Bearer ${token}` },
              params: statusParams
            });
            counts[status] = statusResponse.data.total || 0;
            console.log(`Status "${status}" count (segment parameter + filters):`, counts[status], 'Params:', statusParams);
          } catch (error) {
            console.error(`Failed to load count for status ${status}:`, error);
            counts[status] = 0;
          }
        }
      }
      
      console.log('Final statusCounts:', counts);
      setStatusCounts(counts);
    } catch (error) {
      console.error('Failed to load status counts:', error);
      // Set empty counts on error
      const emptyCounts = {};
      allStatuses.forEach(status => {
        emptyCounts[status] = 0;
      });
      emptyCounts['All'] = 0;
      setStatusCounts(emptyCounts);
    }
  };

  const loadCounts = async () => {
    const token = localStorage.getItem('token');
    try {
      // If filters are active, calculate counts with filters applied
      if (hasActiveFilters) {
        console.log('=== LOAD TAB COUNTS (Filters Active) ===');
        console.log('Active filters:', filters);
        // Map segment display names to API segment values and lowercase keys for counts object
        const segmentMap = [
          { display: 'All', apiValue: 'All', key: 'all' },
          { display: 'New', apiValue: 'New', key: 'new' },
          { display: 'Backlog', apiValue: 'Backlog', key: 'backlog' },
          { display: 'Active', apiValue: 'Active', key: 'active' },
          { display: 'Lost', apiValue: 'Lost', key: 'lost' },
          { display: 'Duplicates', apiValue: 'Duplicates', key: 'duplicates' },
          { display: 'Inactive', apiValue: 'Inactive', key: 'inactive' }
        ];
        const filteredCounts = {};
        
        // Calculate count for each segment with filters applied
        for (const segment of segmentMap) {
          try {
            const params = { segment: segment.apiValue, limit: 1 }; // We only need the total count
            if (filters.project) params.project = filters.project;
            if (filters.source) params.source = filters.source;
            if (filters.salesRep && isAdminOrManager) params.assigned_to = filters.salesRep;
            if (filters.fromDate) params.from_date = filters.fromDate;
            if (filters.toDate) params.to_date = filters.toDate;
            if (searchTerm.trim()) params.search = searchTerm.trim();
            // NOTE: We don't include lead_status here because segment takes priority
            
            const response = await axios.get(`${API}/leads`, {
              headers: { Authorization: `Bearer ${token}` },
              params
            });
            
            // Use lowercase key to match backend response format and tab reading
            filteredCounts[segment.key] = response.data.total || 0;
            console.log(`Segment "${segment.display}" (${segment.key}) count (segment + filters):`, filteredCounts[segment.key], 'Params:', params);
          } catch (error) {
            console.error(`Failed to load count for segment ${segment.display}:`, error);
            filteredCounts[segment.key] = 0;
          }
        }
        
        console.log('Final tab counts (counts state):', filteredCounts);
        setCounts(filteredCounts);
      } else {
        console.log('=== LOAD TAB COUNTS (No Filters) ===');
        // No filters - use backend endpoint (faster)
        const response = await axios.get(`${API}/leads/counts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Tab counts from /leads/counts:', response.data);
        setCounts(response.data);
      }
    } catch (error) {
      console.error('Failed to load counts:', error);
    }
  };

  const loadFilterOptions = async () => {
    const token = localStorage.getItem('token');
    try {
      // Load sources from source-management API (dynamic from database)
      let sourcesData = [];
      try {
        // Try source-management API first (for Super Admin)
        const sourceManagementRes = await axios.get(`${API}/master/source-management`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Extract sources from the response structure: [{source: {value, label}, sub_sources: [...]}]
        sourcesData = sourceManagementRes.data.map(item => item.source).filter(s => s.is_active !== false);
      } catch (sourceError) {
        // Fallback to sources-with-sub-sources for non-Super Admin users
        if (sourceError.response?.status === 403) {
          try {
            const sourcesWithSubRes = await axios.get(`${API}/master/sources-with-sub-sources`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            // Extract sources from the response structure
            sourcesData = sourcesWithSubRes.data.map(item => item.source).filter(s => s.is_active !== false);
          } catch (fallbackError) {
            console.error('Failed to load sources:', fallbackError);
            // Final fallback to simple sources API
            const simpleSourcesRes = await axios.get(`${API}/master/sources`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            sourcesData = simpleSourcesRes.data || [];
          }
        } else {
          console.error('Failed to load sources:', sourceError);
          // Fallback to simple sources API
          try {
            const simpleSourcesRes = await axios.get(`${API}/master/sources`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            sourcesData = simpleSourcesRes.data || [];
          } catch (finalError) {
            console.error('All source APIs failed:', finalError);
          }
        }
      }
      
      // Store full source objects (with value and label) for the filter dropdown
      // This allows us to display labels while using values for filtering
      setAvailableSources(sourcesData);

      if (isSalesRep) {
        // For Sales Reps: Get filter options from their actual leads for projects
        const leadsRes = await axios.get(`${API}/leads?limit=1000`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // FIX: Handle paginated response format
        const myLeads = leadsRes.data.leads || leadsRes.data; // Handle both formats
        
        // Extract unique projects from their leads (sources now come from API above)
        const uniqueProjects = [...new Set(myLeads.flatMap(lead => lead.projects || []))];
        const uniqueStatuses = [...new Set(myLeads.map(lead => lead.lead_status).filter(Boolean))];
        
        setAvailableProjects(uniqueProjects.sort());
        console.log('Sales Rep filter options loaded:', {
          projects: uniqueProjects.length,
          sources: sourcesData.length,
          statuses: uniqueStatuses.length
        });
        // Note: We'll use allStatuses for status filter, but could limit to uniqueStatuses if needed
      } else {
        // For Admin/Manager: Load all options from master data
        const projectsRes = await axios.get(`${API}/projects`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAvailableProjects(projectsRes.data.map(p => p.project_name));

        // Load sales reps (only for admin/manager)
        const repsRes = await axios.get(`${API}/brokers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAvailableSalesReps(repsRes.data.filter(u => u.user_role === 'Sales Rep'));
        
        console.log('Admin filter options loaded:', {
          projects: projectsRes.data.length,
          sources: sourcesData.length,
          salesReps: repsRes.data.filter(u => u.user_role === 'Sales Rep').length
        });
      }
      
      // BUG FIX #2: Load dead reasons for ALL users (not just Admin/Manager)
      // Sales Reps also need this for status changes
      const reasonsRes = await axios.get(`${API}/master/dead-reasons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableDeadReasons(reasonsRes.data);
    } catch (error) {
      console.error('Failed to load filter options:', error);
    }
  };

  // Function to navigate to lead detail
  // Save current pathname before navigating so we can detect when we come back
  const navigateToLeadDetail = (leadId) => {
    console.log('🚀 Navigating to lead detail:', leadId);
    console.log('💾 Current filters before navigation:', filters);
    
    // CRITICAL: The useEffect should have already saved the current state
    // But let's ensure it's saved with the latest values right before navigation
    // Read the current saved state first (in case useEffect hasn't run yet)
    const currentSavedState = sessionStorage.getItem('leadListState');
    let stateToSave;
    
    if (currentSavedState) {
      try {
        const parsed = JSON.parse(currentSavedState);
        // Update with current values to ensure we have the latest
        stateToSave = {
          filters: filters, // Use current filters from closure
          currentSegment: currentSegment,
          currentPage: currentPage,
          showAllFields: showAllFields
        };
      } catch (e) {
        stateToSave = {
          filters: filters,
          currentSegment: currentSegment,
          currentPage: currentPage,
          showAllFields: showAllFields
        };
      }
    } else {
      stateToSave = {
        filters: filters,
        currentSegment: currentSegment,
        currentPage: currentPage,
        showAllFields: showAllFields
      };
    }
    
    console.log('💾 Saving state to sessionStorage:', stateToSave);
    console.log('💾 Filters being saved:', JSON.stringify(stateToSave.filters));
    
    // Save to a separate key that won't be overwritten by auto-save
    sessionStorage.setItem('leadListStateManual', JSON.stringify(stateToSave));
    // Also save to the regular key
    sessionStorage.setItem('leadListState', JSON.stringify(stateToSave));
    // Mark that we just manually saved to prevent auto-save from overwriting
    sessionStorage.setItem('lastManualSaveTime', Date.now().toString());
    
    // Verify it was saved correctly
    const verifySaved = sessionStorage.getItem('leadListStateManual');
    const verifyParsed = JSON.parse(verifySaved);
    console.log('✅ Verified saved state (manual):', verifyParsed);
    console.log('✅ Verified filters in saved state:', JSON.stringify(verifyParsed.filters));
    
    // Save current pathname before navigating (so we can detect navigation back)
    sessionStorage.setItem('lastPathname', location.pathname);
    console.log('📍 Saved current pathname:', location.pathname);
    
    // Reset stateRestored so filters will be restored when we come back
    setStateRestored(false);
    navigate(`/leads/${leadId}`);
  };

  const handleStatusChange = (leadId, currentStatus, selectedNewStatus = '') => {
    const today = new Date().toISOString().split('T')[0];
    const now = new Date().toTimeString().split(' ')[0].substring(0, 5);
    
    // Get the lead to access next_call_date for rescheduling
    const lead = leads.find(l => l.id === leadId);
    
    setStatusChangeData({
      leadId,
      currentStatus,
      newStatus: selectedNewStatus, // Pre-fill with the selected status from dropdown
      next_call_date: '',
      site_visit_number: '',
      site_visit_date: '',
      tentative_visit_date: '',
      comments: '',
      deadReason: '',
      isRescheduling: false,
      oldNextCallDate: lead?.next_call_date || ''
    });
    setShowStatusDialog(true);
  };
  
  // Handle reschedule for Follow up leads
  const handleRescheduleFollowUp = (leadId, currentStatus) => {
    const lead = leads.find(l => l.id === leadId);
    
    setStatusChangeData({
      leadId,
      currentStatus,
      newStatus: 'Follow up',
      next_call_date: lead?.next_call_date ? lead.next_call_date.split('T')[0] : '',
      site_visit_number: '',
      site_visit_date: '',
      tentative_visit_date: '',
      comments: '',
      deadReason: '',
      isRescheduling: true,
      oldNextCallDate: lead?.next_call_date || ''
    });
    setShowStatusDialog(true);
  };

  const submitStatusChange = async () => {
    const token = localStorage.getItem('token');
    const { leadId, newStatus, next_call_date, site_visit_number, site_visit_date, tentative_visit_date, comments, deadReason, isRescheduling, oldNextCallDate } = statusChangeData;

    if (!newStatus) {
      toast.error('Please select a status');
      return;
    }

    try {
      let updateData = {};
      
      // Handle reschedule case (updating next_call_date without status change)
      if (isRescheduling && newStatus === 'Follow up') {
        if (!next_call_date) {
          toast.error('Next call date is required');
          return;
        }
        updateData = {
          next_call_date: next_call_date,
          reschedule_only: true,
          old_next_call_date: oldNextCallDate
        };
        
        // Add comment if provided (optional - can be empty)
        if (comments !== undefined && comments !== null) {
          updateData.comment = comments.trim() || '';
        }
        
        await axios.put(`${API}/leads/${leadId}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Update local state
        setLeads(prev => prev.map(lead => 
          lead.id === leadId ? { ...lead, next_call_date: next_call_date } : lead
        ));
        
        toast.success('Next call date updated successfully');
        setShowStatusDialog(false);
        setStatusChangeData({
          leadId: null,
          newStatus: '',
          next_call_date: '',
          site_visit_number: '',
          site_visit_date: '',
          tentative_visit_date: '',
          comments: '',
          deadReason: '',
          isRescheduling: false,
          oldNextCallDate: ''
        });
        return;
      }
      
      // Normal status change
      updateData = { lead_status: newStatus };
      
      // Add conditional fields based on status
      const statusesRequiringNextCall = ['Attempted - 1', 'Attempted - 2', 'Attempted - 3', 'Follow up', 'Site Visit Planned'];
      
      if (statusesRequiringNextCall.includes(newStatus)) {
        if (!next_call_date) {
          toast.error('Next call date is required for this status');
          return;
        }
        updateData.next_call_date = next_call_date;
      }
      
      if (newStatus === 'Site Visit Done') {
        if (!site_visit_number || !site_visit_date) {
          toast.error('Site visit number and date are required');
          return;
        }
        updateData.site_visit_number = parseInt(site_visit_number);
        updateData.site_visit_date = site_visit_date;
      }
      
      if (newStatus === 'Site Visit Planned') {
        if (!tentative_visit_date) {
          toast.error('Tentative visit date is required');
          return;
        }
        updateData.tentative_visit_date = tentative_visit_date;
      }
      
      if (['Lost', 'Unqualified', 'Dropped after site visit'].includes(newStatus)) {
        if (!deadReason) {
          toast.error('Dead reason is required for this status');
          return;
        }
        updateData.dead_reason = deadReason;
      }
      
      // Add comment if provided (optional - can be empty)
      if (comments !== undefined && comments !== null) {
        updateData.comment = comments.trim() || '';
      }
      
      // Optimistic update - update UI immediately
      setLeads(prev => prev.map(lead => 
        lead.id === leadId ? { ...lead, lead_status: newStatus } : lead
      ));
      
      await axios.put(`${API}/leads/${leadId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Status updated successfully');
      setShowStatusDialog(false);
      // Reset status data
      setStatusChangeData({
        leadId: null,
        newStatus: '',
        next_call_date: '',
        site_visit_number: '',
        site_visit_date: '',
        tentative_visit_date: '',
        comments: '',
        deadReason: '',
        isRescheduling: false,
        oldNextCallDate: ''
      });
      
      // Refresh data in background to ensure accuracy
      loadStatusCounts();
      loadCounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleSelectLead = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) ? prev.filter(id => id !== leadId) : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map(l => l.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLeads.length === 0) {
      toast.error('No leads selected');
      return;
    }

    setDeleteLeadIds(selectedLeads);

    if (selectedLeads.length > 10) {
      setShowCredentialDialog(true);
    } else {
      setShowDeleteDialog(true);
    }
  };

  const handleDownloadSelected = async () => {
    if (selectedLeads.length === 0) {
      toast.error('No leads selected');
      return;
    }

    const token = localStorage.getItem('token');
    try {
      // Get the full lead data for selected leads
      const selectedLeadsData = leads.filter(lead => selectedLeads.includes(lead.id));
      
      // Create CSV content
      const headers = [
        'Lead Number',
        'Customer Name', 
        'Mobile',
        'Email',
        'Projects',
        'Status',
        'Source',
        'Sales Rep',
        'Created Date',
        'Location',
        'Budget Range',
        'Property Type',
        'Entry Method'
      ];
      
      const csvContent = [
        headers.join(','),
        ...selectedLeadsData.map(lead => [
          lead.lead_number || '',
          `"${lead.customer_name || ''}"`, // Wrap in quotes to handle commas
          lead.mobile || '',
          lead.email || '',
          `"${(lead.projects || []).join('; ')}"`, // Join multiple projects
          lead.lead_status || '',
          lead.source || '',
          lead.assigned_to_name || '',
          lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-GB') : '',
          lead.location || '',
          lead.budget_range || '',
          lead.property_type || '',
          lead.entry_method || ''
        ].join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `selected_leads_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Downloaded ${selectedLeads.length} leads successfully`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download leads');
    }
  };

  // CHANGE 6: Bulk reassign functions
  const handleBulkReassign = async () => {
    if (selectedLeads.length === 0) {
      toast.error('Please select leads to reassign');
      return;
    }
    
    // Load active sales reps
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/brokers/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveSalesReps(response.data);
      setShowReassignDialog(true);
    } catch (error) {
      toast.error('Failed to load sales reps');
    }
  };

  const confirmBulkReassign = async () => {
    if (!selectedSalesRep) {
      toast.error('Please select a sales rep');
      return;
    }
    
    const token = localStorage.getItem('token');
    try {
      const response = await axios.post(`${API}/leads/bulk-reassign`, {
        lead_ids: selectedLeads,
        assigned_to: selectedSalesRep
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const salesRepName = activeSalesReps.find(s => s.id === selectedSalesRep)?.full_name || 'selected sales rep';
      toast.success(`${selectedLeads.length} leads reassigned to ${salesRepName}`);
      setSelectedLeads([]);
      setShowReassignDialog(false);
      setSelectedSalesRep('');
      loadLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reassign leads');
    }
  };

  const confirmBulkDelete = async () => {
    const token = localStorage.getItem('token');
    try {
      const payload = { lead_ids: deleteLeadIds };
      if (deleteLeadIds.length > 10) {
        payload.username = credentials.username;
        payload.password = credentials.password;
      }

      await axios.post(`${API}/leads/bulk-delete`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`${deleteLeadIds.length} leads deleted successfully`);
      setSelectedLeads([]);
      setShowDeleteDialog(false);
      setShowCredentialDialog(false);
      setCredentials({ username: '', password: '' });
      loadLeads();
      loadStatusCounts();
      loadCounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete leads');
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const clearFilters = () => {
    setFilters({
      project: '',
      status: '',
      source: '',
      salesRep: '',
      fromDate: '',
      toDate: ''
    });
  };

  const removeFilter = (filterType) => {
    setFilters(prev => ({ ...prev, [filterType]: '' }));
  };

  const getFilterLabel = (filterType) => {
    const labels = {
      project: 'Project',
      status: 'Status',
      source: 'Source',
      salesRep: 'Sales Rep',
      fromDate: 'From Date',
      toDate: 'To Date'
    };
    return labels[filterType] || filterType;
  };

  const getFilterValue = (filterType) => {
    const value = filters[filterType];
    if (!value) return '';
    
    if (filterType === 'salesRep') {
      const rep = availableSalesReps.find(r => r.id === value);
      return rep ? rep.full_name : value;
    }
    
    if (filterType === 'fromDate' || filterType === 'toDate') {
      return new Date(value).toLocaleDateString('en-GB');
    }
    
    return value;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Back button now uses navigate(-1) for proper browser history navigation */}
      <HeaderNav />
      <div className="container mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-800">Lead Management</h1>
          <p className="text-slate-600">View and manage all your leads</p>
        </div>

        {/* Search and Actions */}
        <div className="mb-6 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
              <Input
                type="text"
                placeholder="Search by name, mobile, email, lead number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <Button
              variant="outline"
              onClick={() => setShowAllFields(!showAllFields)}
              className="flex items-center gap-2"
            >
              {showAllFields ? <EyeOff size={16} /> : <Eye size={16} />}
              {showAllFields ? 'Standard View' : 'Detailed View'}
            </Button>
            {isSuperAdmin && selectedLeads.length > 0 && (
              <>
                {/* CHANGE 6: Bulk Reassign Button */}
                <Button
                  variant="outline"
                  onClick={handleBulkReassign}
                  className="flex items-center gap-2 border-cyan-500 text-cyan-700 hover:bg-cyan-50"
                >
                  <UserPlus size={16} />
                  Reassign Selected ({selectedLeads.length})
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDownloadSelected}
                  className="flex items-center gap-2"
                >
                  <Download size={16} />
                  Download Selected ({selectedLeads.length})
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleBulkDelete}
                  className="flex items-center gap-2"
                >
                  <Trash2 size={16} />
                  Delete Selected ({selectedLeads.length})
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-700">Filters</h3>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X size={14} className="mr-1" /> Clear All
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Project Filter */}
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1 block">Project</Label>
              <select
                value={filters.project}
                onChange={(e) => handleFilterChange('project', e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                <option value="">All Projects</option>
                {availableProjects.map(project => (
                  <option key={project} value={project}>{project}</option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1 block">Status</Label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                <option value="">All Statuses</option>
                {allStatuses.map(status => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            {/* Source Filter */}
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1 block">Source</Label>
              <select
                value={filters.source}
                onChange={(e) => handleFilterChange('source', e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                <option value="">All Sources</option>
                {availableSources.map(source => (
                  <option key={source.value} value={source.value}>{source.label}</option>
                ))}
              </select>
            </div>

            {/* Sales Rep Filter (Admin/Manager only) */}
            {isAdminOrManager && (
              <div>
                <Label className="text-xs font-medium text-slate-600 mb-1 block">Sales Rep</Label>
                <select
                  value={filters.salesRep}
                  onChange={(e) => handleFilterChange('salesRep', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                >
                  <option value="">All Sales Reps</option>
                  {availableSalesReps.map(rep => (
                    <option key={rep.id} value={rep.id}>{rep.full_name}</option>
                  ))}
                </select>
              </div>
            )}
            
            {/* MODIFICATION #9: Date Range Filter */}
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1 block">From Date</Label>
              <Input
                type="date"
                value={filters.fromDate}
                onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                className="text-sm"
              />
            </div>
            
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1 block">To Date</Label>
              <Input
                type="date"
                value={filters.toDate}
                onChange={(e) => handleFilterChange('toDate', e.target.value)}
                className="text-sm"
              />
            </div>
          </div>
        </Card>

        {/* Active Filter Chips */}
        {hasActiveFilters && (
          <div className="mb-4 flex flex-wrap gap-2 items-center">
            <span className="text-sm font-medium text-slate-600">Active Filters:</span>
            {filters.project && (
              <Badge variant="secondary" className="flex items-center gap-1 px-3 py-1">
                <span>{getFilterLabel('project')}: {getFilterValue('project')} [{totalLeads}]</span>
                <button
                  onClick={() => removeFilter('project')}
                  className="ml-1 hover:bg-slate-300 rounded-full p-0.5"
                  aria-label="Remove project filter"
                >
                  <X size={12} />
                </button>
              </Badge>
            )}
            {filters.status && (
              <Badge variant="secondary" className="flex items-center gap-1 px-3 py-1">
                <span>{getFilterLabel('status')}: {getFilterValue('status')} [{totalLeads}]</span>
                <button
                  onClick={() => removeFilter('status')}
                  className="ml-1 hover:bg-slate-300 rounded-full p-0.5"
                  aria-label="Remove status filter"
                >
                  <X size={12} />
                </button>
              </Badge>
            )}
            {filters.source && (
              <Badge variant="secondary" className="flex items-center gap-1 px-3 py-1">
                <span>{getFilterLabel('source')}: {getFilterValue('source')} [{totalLeads}]</span>
                <button
                  onClick={() => removeFilter('source')}
                  className="ml-1 hover:bg-slate-300 rounded-full p-0.5"
                  aria-label="Remove source filter"
                >
                  <X size={12} />
                </button>
              </Badge>
            )}
            {filters.salesRep && (
              <Badge variant="secondary" className="flex items-center gap-1 px-3 py-1">
                <span>{getFilterLabel('salesRep')}: {getFilterValue('salesRep')} [{totalLeads}]</span>
                <button
                  onClick={() => removeFilter('salesRep')}
                  className="ml-1 hover:bg-slate-300 rounded-full p-0.5"
                  aria-label="Remove sales rep filter"
                >
                  <X size={12} />
                </button>
              </Badge>
            )}
            {filters.fromDate && (
              <Badge variant="secondary" className="flex items-center gap-1 px-3 py-1">
                <span>{getFilterLabel('fromDate')}: {getFilterValue('fromDate')} [{totalLeads}]</span>
                <button
                  onClick={() => removeFilter('fromDate')}
                  className="ml-1 hover:bg-slate-300 rounded-full p-0.5"
                  aria-label="Remove from date filter"
                >
                  <X size={12} />
                </button>
              </Badge>
            )}
            {filters.toDate && (
              <Badge variant="secondary" className="flex items-center gap-1 px-3 py-1">
                <span>{getFilterLabel('toDate')}: {getFilterValue('toDate')} [{totalLeads}]</span>
                <button
                  onClick={() => removeFilter('toDate')}
                  className="ml-1 hover:bg-slate-300 rounded-full p-0.5"
                  aria-label="Remove to date filter"
                >
                  <X size={12} />
                </button>
              </Badge>
            )}
          </div>
        )}

        {/* Tabs - Show only when no status filter is applied */}
        {!filters.status ? (
          <Tabs value={currentSegment} onValueChange={setCurrentSegment} className="space-y-4">
            <TabsList className="grid w-full grid-cols-7 gap-1">
              <TabsTrigger value="All">
                All [{counts.all || 0}]
              </TabsTrigger>
              <TabsTrigger value="New">
                New [{counts.new || 0}]
              </TabsTrigger>
              <TabsTrigger value="Backlog">
                Backlog [{counts.backlog || 0}]
              </TabsTrigger>
              <TabsTrigger value="Active">
                Active [{counts.active || 0}]
              </TabsTrigger>
              <TabsTrigger value="Lost">
                Lost [{counts.lost || 0}]
              </TabsTrigger>
              <TabsTrigger value="Duplicates">
                Duplicates [{counts.duplicates || 0}]
              </TabsTrigger>
              <TabsTrigger value="Inactive">
                Inactive [{counts.inactive || 0}]
              </TabsTrigger>
            </TabsList>

            <TabsContent value={currentSegment}>
              <Card>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {isSuperAdmin && (
                        <TableHead className="w-12">
                          <Checkbox
                            checked={selectedLeads.length === leads.length && leads.length > 0}
                            onCheckedChange={handleSelectAll}
                          />
                        </TableHead>
                      )}
                      <TableHead>Lead #</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Mobile</TableHead>
                      {showAllFields && <TableHead>Email</TableHead>}
                      <TableHead>Projects</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Source</TableHead>
                      {isAdminOrManager && <TableHead>Sales Rep</TableHead>}
                      {showAllFields && (
                        <>
                          <TableHead>Entry Method</TableHead>
                          <TableHead>Created At</TableHead>
                        </>
                      )}
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={showAllFields ? 12 : 9} className="text-center py-8">
                          Loading leads...
                        </TableCell>
                      </TableRow>
                    ) : leads.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={showAllFields ? 12 : 9} className="text-center py-8 text-slate-500">
                          No leads found
                        </TableCell>
                      </TableRow>
                    ) : (
                      leads.map((lead) => (
                        <TableRow key={lead.id} className="hover:bg-slate-50">
                          {isSuperAdmin && (
                            <TableCell>
                              <Checkbox
                                checked={selectedLeads.includes(lead.id)}
                                onCheckedChange={() => handleSelectLead(lead.id)}
                              />
                            </TableCell>
                          )}
                          <TableCell className="font-medium">{lead.lead_number}</TableCell>
                          <TableCell>{lead.customer_name}</TableCell>
                          <TableCell>{lead.mobile}</TableCell>
                          {showAllFields && <TableCell>{lead.email || '-'}</TableCell>}
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {lead.projects?.slice(0, 2).map((proj, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {proj}
                                </Badge>
                              ))}
                              {lead.projects?.length > 2 && (
                                <Badge variant="outline" className="text-xs">+{lead.projects.length - 2}</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <select
                              value={lead.lead_status}
                              onChange={(e) => {
                                const selectedValue = e.target.value;
                                if (selectedValue === '__reschedule__') {
                                  handleRescheduleFollowUp(lead.id, lead.lead_status);
                                } else if (selectedValue !== lead.lead_status) {
                                  // Open status change dialog with the selected new status pre-filled
                                  handleStatusChange(lead.id, lead.lead_status, selectedValue);
                                }
                              }}
                              disabled={lead.lead_status === 'Booked'}
                              className={`text-xs px-2 py-1 rounded border ${getStatusColor(lead.lead_status)} ${lead.lead_status === 'Booked' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                            >
                              <option value={lead.lead_status}>{lead.lead_status}</option>
                              {lead.lead_status === 'Follow up' && (
                                <option value="__reschedule__">📅 Reschedule Follow up</option>
                              )}
                              {/* All available status options */}
                              {lead.lead_status !== 'New' && <option value="New">New</option>}
                              {lead.lead_status !== 'Attempted - 1' && <option value="Attempted - 1">Attempted - 1</option>}
                              {lead.lead_status !== 'Attempted - 2' && <option value="Attempted - 2">Attempted - 2</option>}
                              {lead.lead_status !== 'Attempted - 3' && <option value="Attempted - 3">Attempted - 3</option>}
                              {lead.lead_status !== 'Site Visit Planned' && <option value="Site Visit Planned">Site Visit Planned</option>}
                              {lead.lead_status !== 'Site Visit Done' && <option value="Site Visit Done">Site Visit Done</option>}
                              {lead.lead_status !== 'Follow up' && <option value="Follow up">Follow up</option>}
                              {lead.lead_status !== 'Booked' && <option value="Booked">Booked</option>}
                              {lead.lead_status !== 'Lost' && <option value="Lost">Lost</option>}
                              {lead.lead_status !== 'Unqualified' && <option value="Unqualified">Unqualified</option>}
                              {lead.lead_status !== 'Dropped after site visit' && <option value="Dropped after site visit">Dropped after site visit</option>}
                            </select>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="text-xs">{lead.source}</Badge>
                          </TableCell>
                          {isAdminOrManager && (
                            <TableCell className="text-sm">{lead.assigned_to_name || '-'}</TableCell>
                          )}
                          {showAllFields && (
                            <>
                              <TableCell>
                                <Badge variant="outline" className="text-xs">{lead.entry_method}</Badge>
                              </TableCell>
                              <TableCell className="text-xs text-slate-500">
                                {lead.created_at ? format(new Date(lead.created_at), 'dd MMM yyyy') : '-'}
                              </TableCell>
                            </>
                          )}
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigateToLeadDetail(lead.id)}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
            
            {/* PAGINATION: Page controls */}
            <div className="flex items-center justify-between mt-4">
              {/* Left: Summary */}
              <div className="text-sm text-gray-600">
                Showing {((currentPage - 1) * limit) + 1}-{Math.min(currentPage * limit, totalLeads)} of {totalLeads} leads
              </div>
              
              {/* Right: Page controls */}
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  size="sm"
                >
                  Previous
                </Button>
                
                {/* Page numbers */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    // Show first 2, last 2, current, and neighbors
                    return page === 1 || page === totalPages || 
                           Math.abs(page - currentPage) <= 1;
                  })
                  .map((page, idx, arr) => (
                    <React.Fragment key={page}>
                      {idx > 0 && arr[idx - 1] !== page - 1 && <span className="text-gray-400">...</span>}
                      <Button
                        variant={page === currentPage ? "default" : "outline"}
                        onClick={() => setCurrentPage(page)}
                        size="sm"
                        className="min-w-[40px]"
                      >
                        {page}
                      </Button>
                    </React.Fragment>
                  ))}
                
                <Button 
                  variant="outline" 
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  size="sm"
                >
                  Next
                </Button>
              </div>
            </div>
            </TabsContent>
          </Tabs>
        ) : (
          /* Lead Table - Show directly when status filter is applied */
          <>
          <Card>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {isSuperAdmin && (
                        <TableHead className="w-12">
                          <Checkbox
                            checked={selectedLeads.length === leads.length && leads.length > 0}
                            onCheckedChange={handleSelectAll}
                          />
                        </TableHead>
                      )}
                      <TableHead>Lead #</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Mobile</TableHead>
                      {showAllFields && <TableHead>Email</TableHead>}
                      <TableHead>Projects</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Source</TableHead>
                      {isAdminOrManager && <TableHead>Sales Rep</TableHead>}
                      {showAllFields && (
                        <>
                          <TableHead>Entry Method</TableHead>
                          <TableHead>Created At</TableHead>
                        </>
                      )}
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={showAllFields ? 12 : 9} className="text-center py-8">
                          Loading leads...
                        </TableCell>
                      </TableRow>
                    ) : leads.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={showAllFields ? 12 : 9} className="text-center py-8 text-slate-500">
                          No leads found
                        </TableCell>
                      </TableRow>
                    ) : (
                      leads.map((lead) => (
                        <TableRow key={lead.id} className="hover:bg-slate-50">
                          {isSuperAdmin && (
                            <TableCell>
                              <Checkbox
                                checked={selectedLeads.includes(lead.id)}
                                onCheckedChange={() => handleSelectLead(lead.id)}
                              />
                            </TableCell>
                          )}
                          <TableCell className="font-medium">{lead.lead_number}</TableCell>
                          <TableCell>{lead.customer_name}</TableCell>
                          <TableCell>{lead.mobile}</TableCell>
                          {showAllFields && <TableCell>{lead.email || '-'}</TableCell>}
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {lead.projects?.slice(0, 2).map((proj, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {proj}
                                </Badge>
                              ))}
                              {lead.projects?.length > 2 && (
                                <Badge variant="outline" className="text-xs">+{lead.projects.length - 2}</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <select
                              value={lead.lead_status}
                              onChange={(e) => {
                                const selectedValue = e.target.value;
                                if (selectedValue === '__reschedule__') {
                                  handleRescheduleFollowUp(lead.id, lead.lead_status);
                                } else if (selectedValue !== lead.lead_status) {
                                  // Open status change dialog with the selected new status pre-filled
                                  handleStatusChange(lead.id, lead.lead_status, selectedValue);
                                }
                              }}
                              disabled={lead.lead_status === 'Booked'}
                              className={`text-xs px-2 py-1 rounded border ${getStatusColor(lead.lead_status)} ${lead.lead_status === 'Booked' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                            >
                              <option value={lead.lead_status}>{lead.lead_status}</option>
                              {lead.lead_status === 'Follow up' && (
                                <option value="__reschedule__">📅 Reschedule Follow up</option>
                              )}
                              {/* All available status options */}
                              {lead.lead_status !== 'New' && <option value="New">New</option>}
                              {lead.lead_status !== 'Attempted - 1' && <option value="Attempted - 1">Attempted - 1</option>}
                              {lead.lead_status !== 'Attempted - 2' && <option value="Attempted - 2">Attempted - 2</option>}
                              {lead.lead_status !== 'Attempted - 3' && <option value="Attempted - 3">Attempted - 3</option>}
                              {lead.lead_status !== 'Site Visit Planned' && <option value="Site Visit Planned">Site Visit Planned</option>}
                              {lead.lead_status !== 'Site Visit Done' && <option value="Site Visit Done">Site Visit Done</option>}
                              {lead.lead_status !== 'Follow up' && <option value="Follow up">Follow up</option>}
                              {lead.lead_status !== 'Booked' && <option value="Booked">Booked</option>}
                              {lead.lead_status !== 'Lost' && <option value="Lost">Lost</option>}
                              {lead.lead_status !== 'Unqualified' && <option value="Unqualified">Unqualified</option>}
                              {lead.lead_status !== 'Dropped after site visit' && <option value="Dropped after site visit">Dropped after site visit</option>}
                            </select>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="text-xs">{lead.source}</Badge>
                          </TableCell>
                          {isAdminOrManager && (
                            <TableCell className="text-sm">{lead.assigned_to_name || '-'}</TableCell>
                          )}
                          {showAllFields && (
                            <>
                              <TableCell>
                                <Badge variant="outline" className="text-xs">{lead.entry_method}</Badge>
                              </TableCell>
                              <TableCell className="text-xs text-slate-500">
                                {lead.created_at ? format(new Date(lead.created_at), 'dd MMM yyyy') : '-'}
                              </TableCell>
                            </>
                          )}
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigateToLeadDetail(lead.id)}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
            
            {/* PAGINATION: Page controls */}
            <div className="flex items-center justify-between mt-4">
              {/* Left: Summary */}
              <div className="text-sm text-gray-600">
                Showing {((currentPage - 1) * limit) + 1}-{Math.min(currentPage * limit, totalLeads)} of {totalLeads} leads
              </div>
              
              {/* Right: Page controls */}
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  size="sm"
                >
                  Previous
                </Button>
                
                {/* Page numbers */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    // Show first 2, last 2, current, and neighbors
                    return page === 1 || page === totalPages || 
                           Math.abs(page - currentPage) <= 1;
                  })
                  .map((page, idx, arr) => (
                    <React.Fragment key={page}>
                      {idx > 0 && arr[idx - 1] !== page - 1 && <span className="text-gray-400">...</span>}
                      <Button
                        variant={page === currentPage ? "default" : "outline"}
                        onClick={() => setCurrentPage(page)}
                        size="sm"
                        className="min-w-[40px]"
                      >
                        {page}
                      </Button>
                    </React.Fragment>
                  ))}
                
                <Button 
                  variant="outline" 
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  size="sm"
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Status Change Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {statusChangeData.isRescheduling ? 'Reschedule Follow Up' : 'Change Lead Status'}
            </DialogTitle>
            <DialogDescription>
              {statusChangeData.isRescheduling ? (
                <span>Update the next call date for this Follow up lead</span>
              ) : (
                <span>Current Status: <strong>{statusChangeData.currentStatus}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Show reschedule info */}
            {statusChangeData.isRescheduling && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Current Status:</strong> Follow up
                </p>
                {statusChangeData.oldNextCallDate && (
                  <p className="text-sm text-amber-700 mt-1">
                    <strong>Current Next Call:</strong> {new Date(statusChangeData.oldNextCallDate).toLocaleDateString('en-GB')}
                  </p>
                )}
              </div>
            )}
            
            {/* Status selection - hidden for rescheduling */}
            {!statusChangeData.isRescheduling && (
              <div>
                <Label>New Status *</Label>
                <select
                  value={statusChangeData.newStatus}
                  onChange={(e) => setStatusChangeData({ ...statusChangeData, newStatus: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md mt-1"
                >
                  <option value="">Select status...</option>
                  {allStatuses
                    .filter(s => s !== statusChangeData.currentStatus)
                    .map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                </select>
              </div>
            )}
            
            {/* Next Call Date - shown for reschedule or when new status requires it */}
            {(statusChangeData.isRescheduling || ['Attempted - 1', 'Attempted - 2', 'Attempted - 3', 'Follow up', 'Site Visit Planned'].includes(statusChangeData.newStatus)) && (
              <div>
                <Label>Next Call Date <span className="text-red-500">*</span></Label>
                <Input
                  type="date"
                  value={statusChangeData.next_call_date}
                  onChange={(e) => setStatusChangeData({ ...statusChangeData, next_call_date: e.target.value })}
                  className="mt-1"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            )}

            {/* Conditional fields based on status - only show when not rescheduling */}
            {statusChangeData.newStatus && !statusChangeData.isRescheduling && (
              <>
                {/* Site Visit Number and Date for Site Visit Done */}
                {statusChangeData.newStatus === 'Site Visit Done' && (
                  <>
                    <div>
                      <Label>Site Visit # <span className="text-red-500">*</span></Label>
                      <select
                        value={statusChangeData.site_visit_number}
                        onChange={(e) => setStatusChangeData({ ...statusChangeData, site_visit_number: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md mt-1"
                      >
                        <option value="">Select visit number...</option>
                        <option value="1">Visit #1</option>
                        <option value="2">Visit #2</option>
                        <option value="3">Visit #3</option>
                        <option value="4">Visit #4</option>
                      </select>
                      <p className="text-xs text-slate-500 mt-1">
                        Note: Visit numbers should be sequential. Use Lead Details for proper visit tracking.
                      </p>
                    </div>
                    <div>
                      <Label>Site Visit Date <span className="text-red-500">*</span></Label>
                      <Input
                        type="date"
                        value={statusChangeData.site_visit_date}
                        onChange={(e) => setStatusChangeData({ ...statusChangeData, site_visit_date: e.target.value })}
                        className="mt-1"
                        max={new Date().toISOString().split('T')[0]} // Allow only past/present dates
                      />
                    </div>
                  </>
                )}

                {/* Tentative Visit Date for Site Visit Planned */}
                {statusChangeData.newStatus === 'Site Visit Planned' && (
                  <div>
                    <Label>Tentative Visit Date <span className="text-red-500">*</span></Label>
                    <Input
                      type="date"
                      value={statusChangeData.tentative_visit_date}
                      onChange={(e) => setStatusChangeData({ ...statusChangeData, tentative_visit_date: e.target.value })}
                      className="mt-1"
                      min={new Date().toISOString().split('T')[0]} // Prevent past dates
                    />
                  </div>
                )}

                {/* Dead Reason dropdown for Lost, Unqualified, Dropped after site visit */}
                {['Lost', 'Unqualified', 'Dropped after site visit'].includes(statusChangeData.newStatus) && (
                  <div>
                    <Label>Dead Reason <span className="text-red-500">*</span></Label>
                    <Select 
                      value={statusChangeData.deadReason} 
                      onValueChange={(value) => setStatusChangeData({ ...statusChangeData, deadReason: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select reason" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableDeadReasons.map(reason => (
                          <SelectItem key={reason.value} value={reason.value}>
                            {reason.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}
            
            {/* Comment field - shown for all status changes and reschedules */}
            {(statusChangeData.newStatus || statusChangeData.isRescheduling) && (
              <div>
                <Label>Comment (Optional)</Label>
                <Textarea
                  value={statusChangeData.comments || ''}
                  onChange={(e) => setStatusChangeData({ ...statusChangeData, comments: e.target.value })}
                  placeholder="Add a comment about this status change..."
                  className="mt-1"
                  rows={3}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusDialog(false)}>
              Cancel
            </Button>
            <Button onClick={submitStatusChange}>
              {statusChangeData.isRescheduling ? 'Update Date' : 'Update Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={20} />
              Confirm Deletion
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Are you sure you want to delete <strong>{deleteLeadIds.length}</strong> lead(s)? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmBulkDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credential Dialog for >10 leads */}
      <Dialog open={showCredentialDialog} onOpenChange={setShowCredentialDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={20} />
              Credential Verification Required
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-600 mb-4">
            You are attempting to delete <strong>{deleteLeadIds.length}</strong> leads. Please enter your credentials to confirm.
          </p>
          <div className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                placeholder="your.email@example.com"
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                placeholder="Enter your password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCredentialDialog(false);
              setCredentials({ username: '', password: '' });
            }}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmBulkDelete}>
              Verify & Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CHANGE 6: Bulk Reassign Dialog */}
      <Dialog open={showReassignDialog} onOpenChange={setShowReassignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reassign Selected Leads</DialogTitle>
            <DialogDescription>
              Reassign {selectedLeads.length} selected lead(s) to a different sales rep.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-sm font-medium mb-2 block">Assign to Sales Rep</Label>
            <Select value={selectedSalesRep} onValueChange={setSelectedSalesRep}>
              <SelectTrigger>
                <SelectValue placeholder="Select a sales rep" />
              </SelectTrigger>
              <SelectContent>
                {activeSalesReps.map(rep => (
                  <SelectItem key={rep.id} value={rep.id}>
                    {rep.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowReassignDialog(false);
              setSelectedSalesRep('');
            }}>
              Cancel
            </Button>
            <Button 
              className="bg-cyan-600 hover:bg-cyan-700"
              onClick={confirmBulkReassign}
              disabled={!selectedSalesRep}
            >
              Reassign Leads
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function getStatusColor(status) {
  const colors = {
    'New': 'bg-blue-50 text-blue-700 border-blue-200',
    'Attempted - 1': 'bg-yellow-50 text-yellow-700 border-yellow-200',
    'Attempted - 2': 'bg-yellow-50 text-yellow-700 border-yellow-200',
    'Attempted - 3': 'bg-yellow-50 text-yellow-700 border-yellow-200',
    'Site Visit Planned': 'bg-purple-50 text-purple-700 border-purple-200',
    'Site Visit Done': 'bg-indigo-50 text-indigo-700 border-indigo-200',
    'Follow up': 'bg-cyan-50 text-cyan-700 border-cyan-200',
    'Booked': 'bg-green-50 text-green-700 border-green-200',
    'Lost': 'bg-red-50 text-red-700 border-red-200',
    'Unqualified': 'bg-gray-50 text-gray-700 border-gray-200',
    'Dropped after site visit': 'bg-orange-50 text-orange-700 border-orange-200'
  };
  return colors[status] || 'bg-slate-50 text-slate-700 border-slate-200';
}

export default LeadList;
